import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import styles from './Signup.module.css';

export default function Signup() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [referredBy, setReferredBy] = useState('');
  const location = useLocation();
  const [requestAffiliate, setRequestAffiliate] = useState(
    () => Boolean(location.state?.fromAffiliate) || document.referrer.includes('/affiliate')
  );
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPasswordHint, setShowPasswordHint] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();

  const affiliateChecklist = [
    'We’ll email you a welcome kit with your referral resources as soon as your account is created.',
    'Complete your payout profile so we know where to send your commissions.',
    'Share your referral link and track performance from your affiliate dashboard.',
  ];

  // Generate a unique affiliate code based on email and timestamp
  const generateAffiliateCode = email => {
    const emailPrefix = email.split('@')[0].replace(/[^a-zA-Z0-9]/g, '');
    const randomPart = Math.floor(Math.random() * 10000)
      .toString()
      .padStart(4, '0');
    return (emailPrefix.substring(0, 6) + randomPart).toLowerCase();
  };

  // Create affiliate record in the system
  const createAffiliateRecord = async email => {
    try {
      // Default name from email (user can update later)
      const name = email.split('@')[0];
      // Only generate code if user requested to be an affiliate
      const code = requestAffiliate ? generateAffiliateCode(email) : null;

      const response = await fetch('/api/affiliates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name,
          email,
          code,
          referred_by: referredBy || null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create affiliate');
      }

      return await response.json();
    } catch (err) {
      console.error('Error creating affiliate:', err);
      // Don't block the signup flow if affiliate creation fails
      return null;
    }
  };

  async function handleSubmit(e) {
    e.preventDefault();

    if (password !== confirmPassword) {
      return setError('Passwords do not match');
    }

    // Check password length without trimming spaces
    if (password.length < 6) {
      setShowPasswordHint(true);
      return setError('Password must be at least 6 characters');
    }

    try {
      setError('');
      setLoading(true);

      // Step 1: Create the user account with Firebase
      const userCredential = await signup(email, password);

      // Step 2: Create the affiliate record
      const affiliateData = await createAffiliateRecord(email);

      // Step 3: Navigate based on affiliate request
      if (requestAffiliate) {
        navigate('/affiliate-application', {
          state: {
            fromSignup: true,
            affiliateData: affiliateData,
          },
        });
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Signup error:', error);
      setError('Failed to create an account. ' + (error.message || ''));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.overlay} />
      <div className={styles.content}>
        <div className={styles.formContainer}>
          <h1 className={styles.title}>Create Account</h1>
          <p className={styles.subtitle}>
            {requestAffiliate
              ? 'We’ll set up your account, get your affiliate tools ready, and email you everything you need to start promoting.'
              : 'Create your MyPeopleDatabase account to access the platform. You can opt into our affiliate program at any time.'}
          </p>

          {error && <div className={styles.error}>{error}</div>}

          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.formGroup}>
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                className={styles.input}
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={e => {
                  setPassword(e.target.value);
                  if (e.target.value.length > 0) {
                    setShowPasswordHint(true);
                  }
                }}
                required
                className={styles.input}
              />
              {showPasswordHint && (
                <small className={styles.passwordHint}>
                  Password must be at least 6 characters
                </small>
              )}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                required
                className={styles.input}
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="referredBy">Referred by (optional)</label>
              <input
                type="email"
                id="referredBy"
                value={referredBy}
                onChange={e => setReferredBy(e.target.value)}
                placeholder="Enter the email of who referred you"
                className={styles.input}
              />
              <small className={styles.fieldHint}>
                If someone referred you to MyPeopleDatabase, enter their email address
              </small>
            </div>

            <div className={styles.checkboxGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={requestAffiliate}
                  onChange={e => setRequestAffiliate(e.target.checked)}
                  className={styles.checkbox}
                />
                <span>Request to be an affiliate</span>
              </label>
              <small className={styles.fieldHint}>
                Check this to join our affiliate program and earn commissions on referrals. We’ll
                confirm by email once your referral link is ready to use.
              </small>
            </div>

            {requestAffiliate && (
              <div className={styles.affiliateOverview}>
                <div className={styles.affiliateOverviewHeader}>
                  <span className={styles.statusBadge}>Affiliate setup</span>
                  <p>
                    You’ll go through a quick two-step process: create your account now and activate
                    your affiliate tools right after.
                  </p>
                </div>
                <ul className={styles.affiliateChecklist}>
                  {affiliateChecklist.map(item => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            <p className={styles.termsNotice}>
              By creating an account, you agree to our{' '}
              <Link to="/terms-of-service" className={styles.link}>
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link to="/privacy-policy" className={styles.link}>
                Privacy Policy
              </Link>
              .
            </p>

            <button type="submit" disabled={loading} className={styles.submitButton}>
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>

          <div className={styles.login}>
            Already have an account?{' '}
            <Link to="/login" className={styles.link}>
              Log In
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
